import { Box, Flex, SimpleGrid, Text, Button, Icon, Skeleton, Grid } from '@chakra-ui/react'
import { Image } from '@/components/HOC/Image'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { MainBanner } from '@/components/templates/Home/Banners/MainBanner/MainBanner'
import { useTranslation, Trans } from 'next-i18next/pages'

export default function Home() {
  return (
    <>
      <MainBanner />
      <LastWinsWidget />
      <Games />
    </>
  )
}

import MinesIcon from '@/public/assets/images/games-icons/mines-icon-96x96.png'
import JackpotIcon from '@/public/assets/images/games-icons/jackpot-icon-96x96.png'
import DoubleIcon from '@/public/assets/images/games-icons/double-icon-96x96.png'
import LootboxIcon from '@/public/assets/images/games-icons/lootbox-icon-96x96.png'
import CoinflipIcon from '@/public/assets/images/games-icons/coinflip-icon-96x96.png'
import RunesIcon from '@/public/assets/images/games-icons/runes-icon-96x96.png'
import PlinkoIcon from '@/public/assets/images/games-icons/plinko-icon-96x96.png'
import GameZoneIcon from '@/public/assets/images/games-icons/game-zone-icon-96x96.png'

const gameIconsMap = {
  mines: MinesIcon,
  jackpot: JackpotIcon,
  double: DoubleIcon,
  coinflip: CoinflipIcon,
  'lootbox-unlimited': LootboxIcon,
  runes: RunesIcon,
  plinko: PlinkoIcon,
  'game-zone': GameZoneIcon,
}

const LastWinsWidget = () => {
  const [wins, setWins] = useState<Array<Win>>([])
  const queue = useRef<Array<Win>>([])
  const [lastBetDate, setLastBetDate] = useState<Date>(new Date(0))

  const paused = useRef(false)

  const jobRef = useRef<any>(null)

  const handleAddBet = (bet: Win) => {
    setWins((prev) => [...prev, bet].slice(-8))
  }

  const apolloClient = useApolloClient()
  const { data } = useAppoloQuery(GET_LAST_WINS, {
    pollInterval: 15_000,
    skipPollAttempt: () => queue.current.length > 5,
    variables: {
      start: lastBetDate,
    },
  })

  useEffect(() => {
    const records = data?.infoLast100WinBets?.nodes
    if (!records || records.length == 0) {
      return
    }

    const bets = records.map(
      (bet) =>
        ({
          game: bet!.game,
          id: bet!.id,
          player: bet?.player,
          timestamp: new Date(bet!.createdAt).getTime(),
          win: {
            amount: bet!.payout,
            denom: bet!.denom,
          },
          payoutUsd: bet!.payoutUsd != null ? String(bet!.payoutUsd) : undefined,
        }) as Win
    )

    Array.from(new Set(bets.map((bets) => bets.player)).keys()).map((x: string) =>
      apolloClient.query({
        query: GET_USER_PROFILE,
        variables: {
          address: x,
        },
      })
    )

    queue.current.push(...bets)

    setLastBetDate(records.at(-1)!.createdAt)

    if (wins.length < 8) {
      const bets = queue.current.splice(0, 8)
      setWins(bets)
    }

    queue.current = queue.current.slice(0, 100)
  }, [data])

  useEffect(() => {
    const func = () => {
      if (!paused.current) {
        const bet = queue.current.shift()
        if (bet) {
          handleAddBet(bet)
        }
      }
      jobRef.current = setTimeout(func, Math.floor(Math.random() * 1000) + 500)
    }

    func()

    return () => {
      if (jobRef.current) {
        clearTimeout(jobRef.current)
      }
    }
  }, [])

  const isLoading = wins.length == 0

  return (
    <Grid mb={{ base: 4, lg: 6, xl: 8 }}>
      <Box overflow={'hidden'}>
        <Flex gap={{ base: 1, md: 3 }}>
          {!isLoading ? (
            <AnimatePresence initial={false}>
              {[...wins].reverse().map((win) => (
                <motion.div
                  key={win.id}
                  layout
                  initial={{ x: -50, y: -50, opacity: 0 }}
                  animate={{ x: 0, y: 0, opacity: 1 }}
                  exit={{ x: 50, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  onHoverStart={() => (paused.current = true)}
                  onHoverEnd={() => (paused.current = false)}
                >
                  <LastWinsBadge game={win} />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <>
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
              <Skeleton
                flexShrink={0}
                startColor={'ninja.500'}
                endColor={'ninja.600'}
                rounded={'20px'}
                h={{ base: '70px', sm: '77px' }}
                w={{ base: '160px', sm: '200px' }}
              />
            </>
          )}
        </Flex>
      </Box>
    </Grid>
  )
}

type Win = {
  id: string
  player: string
  game: string
  win: { amount: string; denom: string }
  payoutUsd?: string
  timestamp: number
}

const gamesLinksMap = {
  mines: '/mines',
  jackpot: '/jackpot',
  double: '/double',
  coinflip: '/coinflip',
  'lootbox-unlimited': '/lootbox',
  runes: '/runes',
  plinko: '/plinko',
  'game-zone': '/game-zone',
}

const LastWinsBadge = ({ game }: { game: Win }) => {
  const { data } = useGetUserProfileByAddress(game.player)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())

  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  const image = gameIconsMap[game.game as keyof typeof gameIconsMap]

  const isUsdcToken = getTokenByDenom(game.win.denom)?.currency === SupportedCurrencies.USDC
  const useFiatEquivalent = !isUsd && !isUsdcToken && !!game.payoutUsd

  const displayWin = useFiatEquivalent
    ? new TokenMoney(game.payoutUsd!, USDC_NEW_DENOM)
    : new TokenMoney(game.win.amount, game.win.denom)

  return (
    <Flex
      as={Link}
      href={gamesLinksMap[game.game as keyof typeof gameIconsMap] || '/'}
      w={'max-content'}
      alignItems={'center'}
      flexShrink={0}
      gap={4}
      py={3}
      px={4}
      pr={3}
      bg={'ninja.600'}
      rounded={'20px'}
      transition={'0.2s'}
      _hover={{
        bg: 'ninja.500',
      }}
      // boxShadow={'0px 7px 36px -18px rgba(148, 121, 255, 0.60) inset, 0px 8px 16px 0px rgba(148, 121, 255, 0.60) inset'}
    >
      <Box>
        <Flex gap={2} alignItems={'center'} mb={1}>
          <AvatarCircle h={{ base: 6, sm: 7 }} w={{ base: 6, sm: 7 }} src={data?.user?.profileAvatar || ''} />
          {isNftOwner ? (
            <GradientText
              numberOfNfts={numberOfNfts!}
              props={{
                fontWeight: 700,
                fontSize: 'sm',
              }}
            >
              {data?.user?.profileUserName}
            </GradientText>
          ) : (
            <Text color={'ninja.100'} fontSize={{ base: 'xs', sm: 'sm' }} fontWeight={'700'}>
              {data?.user?.profileUserName}
            </Text>
          )}
        </Flex>
        <FiatOrCryptoAmount value={displayWin} wrapperStyles={{ fontSize: { base: 'xs', sm: 'sm' } }} />
      </Box>
      {!!image && <Image flexShrink={0} w={{ base: 10, sm: 12 }} h={{ base: 10, sm: 12 }} src={image} alt={''} />}
    </Flex>
  )
}

import ArrowIcon from '@/public/assets/icons/arrow.svg'
import PlinkoBg from '@/public/assets/images/home/plinko.jpg'
import JackpotBg from '@/public/assets/images/home/jackpot.jpg'
import DoubleBg from '@/public/assets/images/home/double.jpg'
import CoinflipBg from '@/public/assets/images/home/coinflip.jpg'
import MinesBg from '@/public/assets/images/home/mines.jpg'
import LootboxBg from '@/public/assets/images/home/lootbox.jpg'
import RunesBg from '@/public/assets/images/home/runes-bg.jpg'
import GameZoneBg from '@/public/assets/images/home/game-zone-bg.jpg'

import { StaticImageData } from 'next/image'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { GET_LAST_WINS, GET_USER_PROFILE, useGetUserProfileByAddress } from '@/api/query'
import { useApolloClient, useQuery as useAppoloQuery } from '@apollo/client'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { getTokenByDenom } from '@/services/token-service'
import { USDC_NEW_DENOM } from '@/constants/constants'

const Games = () => {
  const { t } = useTranslation()

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={{ base: 2, lg: 4, xl: 6 }}>
      <GamePanel title={t('games.double')} image={DoubleBg} link={'/double'} />
      <GamePanel title={t('games.coinflip')} image={CoinflipBg} link={'/coinflip'} />
      <GamePanel title={t('games.mines')} image={MinesBg} link={'/mines'} />
      <GamePanel title={t('games.lootbox')} image={LootboxBg} link={'/lootbox'} />
      <GamePanel title={t('games.jackpot')} image={JackpotBg} link={'/jackpot'} isFree={true} />
      <GamePanel title={t('games.runes')} scale={1.15} image={RunesBg} link={'/runes'} />
      <GamePanel title={t('games.plinko')} image={PlinkoBg} link={'/plinko'} />
      <GamePanel title={t('games.game_zone')} image={GameZoneBg} link={'/game-zone'} />
    </SimpleGrid>
  )
}

const GamePanelWrapper = ({
  link,
  isDisabled,
  children,
}: {
  link: string
  isDisabled: boolean
  children: React.ReactElement
}) => {
  if (isDisabled) {
    return children
  }

  return <Link href={link}>{children}</Link>
}

const GamePanel = ({
  title,
  isDisabled = false,
  isComingSoon = false,
  isFree = false,
  image,
  link,
  scale = 1.2,
}: {
  title: string
  isDisabled?: boolean
  isComingSoon?: boolean
  isFree?: boolean
  image: StaticImageData
  link: string
  scale?: number
}) => {
  return (
    <Box rounded={'24px'} overflow={'hidden'}>
      <GamePanelWrapper isDisabled={isDisabled} link={link}>
        <Flex
          pos={'relative'}
          opacity={isDisabled ? 0.6 : 1}
          flexDir={'column'}
          alignItems={'start'}
          justifyContent={'space-between'}
          bg={'ninja.500'}
          aspectRatio={1}
          p={{ base: 5, sm: 6, md: 5, lg: 6 }}
          sx={{
            ':hover button': {
              color: 'white',
              bg: 'whiteAlpha.300',
            },
            ':hover img': {
              transform: `scale(${scale || 1.2})`,
            },
          }}
        >
          <Image transition={'0.2s'} transformOrigin={'bottom right'} pos={'absolute'} inset={0} alt="" src={image} />
          <Flex flexWrap={'wrap'} rowGap={2} columnGap={2.5} zIndex={10} alignItems={'center'}>
            <Text lineHeight={1} pos={'relative'} fontWeight={700} fontSize={'26px'}>
              {title}
            </Text>
            {isComingSoon && (
              <Box px={2} py={0.5} rounded={'full'} fontSize={'xs'} fontWeight={700} bg={'ninja.300'} color={'white'}>
                <Trans i18nKey="common.coming-soon"></Trans>
              </Box>
            )}
            {isFree && (
              <Box
                px={2}
                py={0.5}
                rounded={'full'}
                fontSize={'xs'}
                fontWeight={700}
                bg={'brand.yellow'}
                color={'ninja.600'}
              >
                FREE
              </Box>
            )}
          </Flex>
          {!isDisabled && (
            <Button
              bg={'whiteAlpha.200'}
              rounded={'2xl'}
              boxSize={10}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              variant={'unstyled'}
              w={'auto'}
              color={'ninja.200'}
            >
              <Icon transform={'rotate(180deg)'} as={ArrowIcon} display={'block'} w={6} h={6} />
            </Button>
          )}
        </Flex>
      </GamePanelWrapper>
    </Box>
  )
}
